import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Input,
  Segment,
  Loader,
  Message,
  Button,
  Icon,
  Label,
  Card
} from "semantic-ui-react";
import { debounce } from "lodash";

import { useIsMobile } from "../../hooks";
import { Metrics } from "../../components/common/Metrics";

import {
  initializeCheckIn,
  updateSearchTerm,
  updateSearchResults,
  cleanupCheckIn,
  addDebugLog
} from "./actions";
import { SearchIndexEntry, SearchOptions, SearchOptionsStatus } from "./types";
import { SearchResult } from "./components/SearchResult";
import { DebugConsole } from "./components/DebugConsole";

import "./CheckInPage.css";
import { Chip } from "../../components/common/Chip";

interface CheckInPageProps { }

export const CheckInPage: React.FC<CheckInPageProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const isMobile = useIsMobile();

  const {
    campaign,
    searchResults,
    searchIndex,
    isInitialLoading,
    isSyncing,
    lastSyncTime,
    stats,
    errors,
    pendingCheckIns
  } = useSelector((state: any) => state.checkIn || {});

  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    status: SearchOptionsStatus.NOT_CHECKED_IN,
    maxResults: 50,
  });
  const [totalResults, setTotalResults] = useState(0);
  const [showNoResults, setShowNoResults] = useState(false);
  const [timeSinceSync, setTimeSinceSync] = useState(0);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (!searchIndex) return;

      if (!term.trim()) {
        dispatch(updateSearchResults([]));
        setShowNoResults(false);
        return;
      }

      const { total, results } = searchIndex.search(term, searchOptions);

      // Add searchTerm to results for highlighting
      const resultsWithSearchTerm = results.map((result: SearchIndexEntry) => ({
        ...result,
        searchTerm: term
      }));

      dispatch(updateSearchResults(resultsWithSearchTerm));
      setTotalResults(total);
      setShowNoResults(term.trim().length > 0 && total === 0);

      dispatch(addDebugLog("info", `Search "${term}": ${total} results`) as any);
    }, 300),
    [searchOptions, searchIndex, dispatch]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  };

  const handleSearch = (term: string) => {
    setLocalSearchTerm(term);
    dispatch(updateSearchTerm(term) as any);
    debouncedSearch(term);
  };

  const handleSearchStatusChange = (status: SearchOptionsStatus) => {
    setSearchOptions(opts => ({
      ...opts,
      status,
    }));
  }

  // Initialize on mount
  useEffect(() => {
    if (!campaignId) {
      navigate("/campaigns");
      return;
    }

    dispatch(initializeCheckIn(campaignId) as any);

    // Cleanup on unmount
    return () => {
      dispatch(cleanupCheckIn() as any);
    };
  }, [campaignId, dispatch, navigate]);

  // Error handling
  useEffect(() => {
    if (errors?.initialization) {
      console.error("Check-in initialization error:", errors.initialization);
    }
  }, [errors]);

  // Re-run search when data is updated (e.g., from sync)
  useEffect(() => {
    if (localSearchTerm.trim() && searchIndex && lastSyncTime) {
      // Re-run the search to update results with latest data
      const { total, results } = searchIndex.search(localSearchTerm, searchOptions);
      const resultsWithSearchTerm = results.map((result: SearchIndexEntry) => ({
        ...result,
        searchTerm: localSearchTerm
      }));

      dispatch(updateSearchResults(resultsWithSearchTerm));
      setTotalResults(total);
      setShowNoResults(localSearchTerm.trim().length > 0 && total === 0);

      dispatch(addDebugLog("info", `Search refreshed after sync: ${total} results`) as any);
    }
  }, [lastSyncTime, searchIndex, localSearchTerm, searchOptions, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => setTimeSinceSync(prev => prev + 1), 1000);

    return () => clearInterval(interval);
  }, [])

  useEffect(() => {
    if (isSyncing) setTimeSinceSync(0);
  }, [isSyncing]);

  // Format last sync time
  const formatLastSync = () => {
    if (timeSinceSync < 60) return `${timeSinceSync}s ago`;
    if (timeSinceSync < 3600) return `${Math.floor(timeSinceSync / 60)}m ago`;
    return new Date(lastSyncTime).toLocaleTimeString();
  };

  // Loading state
  if (isInitialLoading) {
    return (
      <Container className="checkin-loading-container">
        <Segment basic textAlign="center" style={{ minHeight: "50vh" }}>
          <Loader active size="large">
            Loading check-in data...
          </Loader>
          <div style={{ marginTop: "20px" }}>
            <p>Fetching campaign and sales information...</p>
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              This may take a moment for large events
            </p>
          </div>
        </Segment>
        <DebugConsole />
      </Container>
    );
  }

  // Error state
  if (errors?.initialization) {
    return (
      <Container>
        <Message error>
          <Message.Header>Failed to load check-in data</Message.Header>
          <p>{errors.initialization}</p>
          <Button primary onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Message>
        <DebugConsole />
      </Container>
    );
  }

  // Main check-in interface
  return (
    <Container fluid className="checkin-page">
      <div className="checkin-header">
        <div className="checkin-title-section">
          <h1 className="checkin-title">
            Check-in
          </h1>
          {campaign && (
            <p className="campaign-subtitle">
              {campaign.name}
            </p>
          )}
        </div>

        <div className="checkin-sync-status">
          <Label
            color={isSyncing ? "yellow" : "green"}
            size="small"
            className="sync-label"
          >
            <Icon name={isSyncing ? "sync" : "check"} />
            {isSyncing ? "Syncing..." : `Synced ${formatLastSync()}`}
          </Label>
        </div>
      </div>

      {stats && (
        <Metrics
          items={(() => {
            const checkedInPercentage = stats.totalEntries > 0
              ? Math.round((stats.checkedInEntries / stats.totalEntries) * 100)
              : 0;

            return [
              {
                title: "Checked-in",
                value: isMobile
                  ? `${stats.checkedInEntries} (${checkedInPercentage}%)`
                  : stats.checkedInEntries,
                color: "var(--gr-accent-blush)",
              },
              {
                title: "Attendees",
                value: stats.totalEntries,
                color: "var(--gr-accent-clay)",
              },
              ...(!isMobile ? [
                {
                  title: "Complete",
                  value: `${checkedInPercentage}%`,
                  color: "var(--gr-accent-sage)",
                },
                {
                  title: "Sales",
                  value: stats.totalSales,
                  color: "var(--gr-accent-mint)",
                },
              ] : [])
            ];
          })()}
        />
      )}

      <div className="checkin-search-section">
        <h3 className="search-title">
          {isMobile ? "Find an attendee to check-in" : "Find an attendee to check-in individually or as a group"}
        </h3>
        <Input
          fluid
          autoFocus
          size="large"
          icon={localSearchTerm ? (
            <Icon
              link
              name="x"
              onClick={() => handleSearch("")}
            />
          ) : undefined}
          placeholder={isMobile ? "Search by attendee name" : "Search by attendee name, sale ID or email address"}
          value={localSearchTerm}
          onChange={handleSearchChange}
          className="checkin-search-input"
        />
        <div>
          <Chip
            positive={searchOptions.status === SearchOptionsStatus.NOT_CHECKED_IN}
            onClick={() => handleSearchStatusChange(SearchOptionsStatus.NOT_CHECKED_IN)}
          >
            NOT-CHECKED-IN
          </Chip>
          <Chip
            positive={searchOptions.status === SearchOptionsStatus.ALL}
            onClick={() => handleSearchStatusChange(SearchOptionsStatus.ALL)}
          >
            ALL
          </Chip>
        </div>
      </div>

      {/* Search Results */}
      <div className="checkin-results-section">
        {showNoResults ? (
          <div className="results-header">
            <h4>
              No results found
            </h4>
          </div>
        ) : searchResults.length > 0 ? (
          <>
            <div className="results-header">
              <h4>
                {totalResults} result{totalResults !== 1 ? "s" : ""} found
                {searchResults.length < totalResults && ` (showing first ${searchOptions.maxResults})`}
              </h4>
            </div>

            <Card fluid className="results-list">
              {searchResults.map((result: SearchIndexEntry) => (
                <SearchResult
                  key={result.id}
                  result={result}
                  pendingCheckIns={pendingCheckIns || {}}
                  errors={errors || {}}
                />
              ))}
            </Card>
          </>
        ) : null}
      </div>

      {/* Debug Console */}
      {/* <DebugConsole /> */}
    </Container>
  );
};
